// ============================================================================
// dataTypes/liveStreamManagment/audioPipeline/pitchShiftWorklet.ts
//
// Pitch shifter temps réel — déplacement de hauteur (grave/aigu) SANS changer
// la vitesse de lecture, contrairement à un simple playbackRate. Web Audio n'a
// aucun nœud natif pour ça (contrairement au filtre passe-haut du lowcut) :
// c'est un AudioWorkletProcessor, chargé une fois par AudioContext (cf.
// ensurePitchWorkletLoaded dans AudioSourceRegistry.ts).
//
// Technique : synthèse granulaire à 2 grains en recouvrement (fenêtres
// triangulaires déphasées d'une demi-longueur, qui se somment à ~1 — pas de
// trou ni de bosse de niveau au raccord). Chaque grain relit un anneau
// d'échantillons à une vitesse `pitchRatio` différente de la vitesse
// d'écriture (1 échantillon/échantillon), ce qui change la hauteur perçue ;
// il se recale périodiquement sur "maintenant moins une longueur de grain"
// pour ne jamais dériver ni lire des données pas encore écrites.
//
// Qualité : artefacts audibles (léger grain/robotique) au-delà d'un octave —
// acceptable pour un effet voix grave/aiguë (±12 demi-tons), pas pensé pour
// du mastering. Le code tourne dans le thread audio (pas d'accès DOM/React) :
// str brute injectée comme module via Blob URL, pas de fichier statique à
// servir séparément (CRA ne configure rien pour les AudioWorklet par défaut).
// ============================================================================

export const PITCH_WORKLET_NAME = 'pitch-shift-processor';

const WORKLET_SOURCE = `
class PitchShiftProcessor extends AudioWorkletProcessor {
  static get parameterDescriptors() {
    return [{ name: 'pitchRatio', defaultValue: 1, minValue: 0.5, maxValue: 2, automationRate: 'k-rate' }];
  }

  constructor() {
    super();
    this.bufferSize = 8192; // anneau — largement plus grand qu'un grain
    this.grainSize  = 2048; // ~43ms à 48kHz : compromis qualité/latence
    this.channels   = [];   // état par canal, créé à la volée (mono ou stéréo)
  }

  ensureChannel(ch) {
    let state = this.channels[ch];
    if (state) return state;
    state = {
      ring: new Float32Array(this.bufferSize),
      writeIndex: 0,
      grains: [
        { index: 0, age: 0 },
        { index: 0, age: this.grainSize / 2 },
      ],
    };
    this.channels[ch] = state;
    return state;
  }

  readRing(ring, pos) {
    const size = ring.length;
    let p = pos % size;
    if (p < 0) p += size;
    const i0 = Math.floor(p);
    const i1 = (i0 + 1) % size;
    const frac = p - i0;
    return ring[i0] * (1 - frac) + ring[i1] * frac;
  }

  process(inputs, outputs, parameters) {
    const input  = inputs[0];
    const output = outputs[0];
    if (!input || !input.length) return true;

    const ratio = parameters.pitchRatio[0] ?? 1;
    const half  = this.grainSize / 2;

    for (let ch = 0; ch < input.length; ch++) {
      const inCh  = input[ch];
      const outCh = output[ch];
      if (!inCh || !outCh) continue;

      const state  = this.ensureChannel(ch);
      const ring   = state.ring;
      const grains = state.grains;

      for (let i = 0; i < inCh.length; i++) {
        ring[state.writeIndex] = inCh[i];
        state.writeIndex = (state.writeIndex + 1) % this.bufferSize;

        let sample = 0;
        for (let g = 0; g < grains.length; g++) {
          const grain = grains[g];
          const w = 1 - Math.abs(grain.age - half) / half;
          sample += this.readRing(ring, grain.index) * w;
          grain.index += ratio;
          grain.age += 1;
          if (grain.age >= this.grainSize) {
            grain.age -= this.grainSize; // conserve le déphasage relatif entre les 2 grains
            grain.index = state.writeIndex - this.grainSize;
          }
        }
        outCh[i] = sample;
      }
    }
    return true;
  }
}

registerProcessor('${PITCH_WORKLET_NAME}', PitchShiftProcessor);
`;

let cachedBlobUrl: string | null = null;

/** URL (Blob) du module worklet — créée une seule fois, réutilisable pour tout AudioContext. */
export function getPitchWorkletUrl(): string {
  if (cachedBlobUrl) return cachedBlobUrl;
  const blob = new Blob([WORKLET_SOURCE], { type: 'application/javascript' });
  cachedBlobUrl = URL.createObjectURL(blob);
  return cachedBlobUrl;
}

/** demi-tons → ratio de vitesse de lecture du grain (1 = hauteur inchangée). */
export function semitonesToPitchRatio(semitones: number): number {
  return Math.pow(2, semitones / 12);
}
